import { Button, ChakraProps, HStack } from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import { Icon } from '@app/components/Icon';
import styles from './BuyNowButton.module.scss';
import cn from 'clsx';
import { useNftMarketplaceFiltersState } from '@app/state/nftMarketplaceFiltersStore';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { useCartStore } from '@app/state/nftCartStore';
import { useAddress } from '@thirdweb-dev/react';
import { useNftCartModal } from '@app/context/NftCartContext';

type Props = {
  classes?: {
    root?: string;
    buyButton?: string;
    cartButton?: string;
    icon?: string;
    addedToCart?: string;
    removeFromCart?: string;
  };
  listing: NFTMarketplaceListing;
  variant?: 'rowCompact' | 'secondary' | 'primary';
  activeSxProps?: ChakraProps;
  inactiveSxProps?: ChakraProps;
  buyButtonRestProps?: ChakraProps;
  cartButtonRestProps?: ChakraProps;
  removeButtonRestProps?: ChakraProps;
} & ChakraProps;

export const BuyNowButton: FC<Props> = ({
  classes,
  listing,
  variant,
  activeSxProps = {},
  inactiveSxProps = {},
  buyButtonRestProps = {},
  cartButtonRestProps = {},
  removeButtonRestProps = {},
  ...rest
}) => {
  const address = useAddress();
  const { add, isInTheCart, remove } = useCartStore();
  const { toggleModal } = useNftCartModal();
  const { isEligibleToParticipate, isEligibleToParticipateInPremium } =
    useNftMarketplaceFiltersState();
  const isLive = useMemo(() => {
    if (!listing || !listing.isNFTAvailable) {
      return false;
    }

    if (listing.nftMetadata.isPremium && !isEligibleToParticipateInPremium) {
      return false;
    }

    const now = Date.now();

    if (+listing.start && +listing.end) {
      return now >= listing.start * 1000 && now < listing.end * 1000;
    }

    return true;
  }, [listing, isEligibleToParticipateInPremium]);

  if (
    address?.toLowerCase() === listing.maker?.toLowerCase() ||
    !listing.isNFTAvailable
  ) {
    return null;
  }

  return !isInTheCart(listing.id) ? (
    <HStack className={cn(styles.root, classes?.root)} gap={0} {...rest}>
      <Button
        variant={variant}
        className={cn(styles.buyNowButton, classes?.buyButton)}
        isDisabled={!isEligibleToParticipate || !isLive}
        onClick={async e => {
          e.stopPropagation();
          e.preventDefault();
          add({ listingId: listing.id });
          toggleModal(true);
        }}
        sx={inactiveSxProps}
        {...buyButtonRestProps}
      >
        Buy Now
      </Button>
      <Button
        variant={variant}
        className={cn(styles.cartButton, classes?.cartButton)}
        isDisabled={!isEligibleToParticipate || !isLive}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          add({ listingId: listing.id });
        }}
        sx={inactiveSxProps}
        {...cartButtonRestProps}
      >
        <Icon className={cn(styles.icon)} name="cartFilled" />
      </Button>
    </HStack>
  ) : (
    <Button
      className={cn(styles.removeFromCart, classes?.removeFromCart)}
      onClick={event => {
        event.preventDefault();
        remove(listing.id);
      }}
      variant={variant}
      {...activeSxProps}
      {...removeButtonRestProps}
    >
      Remove from Cart
    </Button>
  );
};
